/**
 * The live budget figure.
 *
 * `POST /budget/calculate` is read-only and idempotent, which is what lets the form call it on
 * every namelist change. Two calls, deliberately:
 *
 *   - **The roster's figures** — every member of the department, so each row can show its own
 *     cap, used, held and available whether or not that person is selected. Cached per
 *     (department, date): a person's entitlement does not depend on who else is coming.
 *   - **The selection's pool** — debounced, because this one moves with every checkbox.
 *
 * One call cannot do both: the pool is computed for exactly the ids given, so asking for the
 * whole department would return the whole department's pool rather than the selection's.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CalculatedAttendee, Calculation } from '@/lib/types';

/** Hold a value still for `delay` ms of quiet — the checkbox equivalent of stopping typing. */
export function useDebounced<T>(value: T, delay = 350): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

export interface LiveBudget {
  /** The selection's pool and issues. Undefined until at least one person is selected. */
  calculation: Calculation | undefined;
  /** Per-employee figures for the roster *and* the selection, selection winning. */
  figures: Map<number, CalculatedAttendee>;
  pending: boolean;
  error: unknown;
}

export function useLiveBudget(input: {
  departmentId: number | null;
  eventDate: string;
  employeeIds: number[];
  /** All the department's member ids, for the roster-wide figures. */
  rosterIds: number[];
  enabled?: boolean;
}): LiveBudget {
  const enabled = (input.enabled ?? true) && input.departmentId !== null && input.eventDate !== '';

  // Sorted so [3,1] and [1,3] are one cache entry rather than two.
  const selectionKey = useMemo(
    () => [...input.employeeIds].sort((a, b) => a - b),
    [input.employeeIds],
  );
  const settled = useDebounced(selectionKey);
  const rosterKey = useMemo(() => [...input.rosterIds].sort((a, b) => a - b), [input.rosterIds]);

  const roster = useQuery({
    queryKey: ['calculate-roster', input.departmentId, input.eventDate, rosterKey],
    queryFn: () =>
      api.calculate({
        department_id: input.departmentId as number,
        date: input.eventDate,
        employee_ids: rosterKey,
      }),
    enabled: enabled && rosterKey.length > 0,
    staleTime: 60_000,
  });

  const selection = useQuery({
    queryKey: ['calculate-selection', input.departmentId, input.eventDate, settled],
    queryFn: () =>
      api.calculate({
        department_id: input.departmentId as number,
        date: input.eventDate,
        employee_ids: settled,
      }),
    enabled: enabled && settled.length > 0,
  });

  const figures = useMemo(() => {
    const merged = new Map<number, CalculatedAttendee>();
    for (const attendee of roster.data?.attendees ?? []) merged.set(attendee.employee_id, attendee);
    // The selection's figures win: they cover people the roster call never saw — an added
    // Worker from another team — and they are no older.
    for (const attendee of selection.data?.attendees ?? []) {
      merged.set(attendee.employee_id, attendee);
    }
    return merged;
  }, [roster.data, selection.data]);

  return {
    calculation: selection.data,
    figures,
    // The roster call is background detail; only the selection's staleness is worth a spinner
    // beside the pool, plus the gap between a click and the debounce firing.
    pending:
      selection.isFetching ||
      (settled.length !== selectionKey.length && selectionKey.length > 0) ||
      settled.join() !== selectionKey.join(),
    error: selection.error ?? roster.error,
  };
}
